// src/components/Questionnaires/TimeManagementPartA.js

import React, {useEffect, useState} from 'react';
import {Card, Form, Input, Button, Alert, Typography, message, Spin} from 'antd';
import moment from 'moment';
import "./TimeManagementPartA.css";

const {Title, Text} = Typography;
const {TextArea} = Input;

const TimeManagementPartA = ({server_url, accessToken}) => {
    const [form] = Form.useForm();
    const [isEligible, setIsEligible] = useState(true);
    const [eligibilityMessage, setEligibilityMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [latestSubmission, setLatestSubmission] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch eligibility and latest submission on component mount
    useEffect(() => {
        const checkEligibility = async () => {
            try {
                // Check Eligibility
                const eligibilityResponse = await fetch(`${server_url}/questionnaires/time-management-partA/eligibility`, {
                    method: 'GET',
                    headers: {Authorization: `Bearer ${accessToken}`},
                });

                if (!eligibilityResponse.ok) {
                    throw new Error('Failed to check eligibility.');
                }

                const eligibilityData = await eligibilityResponse.json();
                setIsEligible(eligibilityData.eligible);
                setEligibilityMessage(eligibilityData.message);

                if (eligibilityData.eligible) {
                    // Fetch the latest submission
                    const latestResponse = await fetch(`${server_url}/questionnaires/time-management-partA/latest`, {
                        method: 'GET',
                        headers: {Authorization: `Bearer ${accessToken}`},
                    });

                    if (!latestResponse.ok) {
                        throw new Error('Failed to fetch latest submission.');
                    }

                    const latestData = await latestResponse.json();
                    if (latestData.latest_submission) {
                        setLatestSubmission(latestData.latest_submission);
                        form.setFieldsValue(latestData.latest_submission.qna);
                    }
                }
            } catch (err) {
                console.error(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        checkEligibility();
    }, [server_url, accessToken, form]);

    // Handle form submission
    const handleSubmit = async (values) => {
        setIsSubmitting(true);
        try {
            const response = await fetch(`${server_url}/questionnaires/time-management-partA/submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    form_data: {
                        answers: values,
                    },
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to submit Time Management Part A.');
            }

            const data = await response.json();
            message.success('Time Management Part A submitted successfully!');
            setIsEligible(false);
            setEligibilityMessage(`Next eligible date: ${moment(data.next_eligible_date).format('YYYY-MM-DD')}`);
            // Optionally, you can reset the form or keep the latest submission displayed
        } catch (err) {
            console.error('Error submitting Time Management Part A:', err);
            message.error(err.message || 'An error occurred while submitting the form.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return <Spin tip="Loading..."/>;
    }

    if (error) {
        return <Alert message={error} type="error" showIcon/>;
    }

    return (
        <Card>
            <div className="tm-container">
                <h2 className="tm-header">PART A (3) - Time Management</h2>

                <p><strong>Tick (&#x2714;) the most appropriate option.</strong></p>

                <table className="tm-option-table">
                    <tr>
                        <th className="tm-th-statement">Statements</th>
                        <th className="tm-th-option">Rarely</th>
                        <th className="tm-th-option">Sometimes</th>
                        <th className="tm-th-option">Usually</th>
                        <th className="tm-th-option">Almost always</th>
                    </tr>
                    <tr>
                        <td className="tm-td-option">I like following a schedule.</td>
                        <td className="tm-td-checkbox"><input type="checkbox"/></td>
                        <td className="tm-td-checkbox"><input type="checkbox"/></td>
                        <td className="tm-td-checkbox"><input type="checkbox"/></td>
                        <td className="tm-td-checkbox"><input type="checkbox"/></td>
                    </tr>
                    <tr>
                        <td className="tm-td-option-alt">I get distracted and do not start my tasks on time.</td>
                        <td className="tm-td-checkbox"><input type="checkbox"/></td>
                        <td className="tm-td-checkbox"><input type="checkbox"/></td>
                        <td className="tm-td-checkbox"><input type="checkbox"/></td>
                        <td className="tm-td-checkbox"><input type="checkbox"/></td>
                    </tr>
                    <tr>
                        <td className="tm-td-option">I do things at the last minute.</td>
                        <td className="tm-td-checkbox"><input type="checkbox"/></td>
                        <td className="tm-td-checkbox"><input type="checkbox"/></td>
                        <td className="tm-td-checkbox"><input type="checkbox"/></td>
                        <td className="tm-td-checkbox"><input type="checkbox"/></td>
                    </tr>
                    <tr>
                        <td className="tm-td-option">I can figure out how much time assignments and projects will
                            take.
                        </td>
                        <td className="tm-td-checkbox"><input type="checkbox"/></td>
                        <td className="tm-td-checkbox"><input type="checkbox"/></td>
                        <td className="tm-td-checkbox"><input type="checkbox"/></td>
                        <td className="tm-td-checkbox"><input type="checkbox"/></td>
                    </tr>
                    <tr>
                        <td className="tm-td-option">I am good at deciding which tasks to do first.</td>
                        <td className="tm-td-checkbox"><input type="checkbox"/></td>
                        <td className="tm-td-checkbox"><input type="checkbox"/></td>
                        <td className="tm-td-checkbox"><input type="checkbox"/></td>
                        <td className="tm-td-checkbox"><input type="checkbox"/></td>
                    </tr>
                </table>

                <div className="tm-habits-banner">My Time Habits</div>

                <table className="tm-habits-table">
                    <tr>
                        <th className="tm-th-habit">What time of the day is best for you?</th>
                        <th className="tm-th-habit">How much time do you spend on schoolwork after school?</th>
                        <th className="tm-th-habit">How much time do you spend on physical fitness?</th>
                        <th className="tm-th-habit">How much time do you spend on other things? (with friends, on your
                            phone, etc.)
                        </th>
                    </tr>
                    <tr>
                        <td className="tm-td-habit">
                            <label>... to study?</label><br/>
                            <label>... to play/exercise?</label><br/>
                            <label>... to relax?</label><br/>
                            <label>... to spend time with friends and family?</label><br/>
                            <textarea className="tm-textarea-habit1"></textarea>
                        </td>
                        <td className="tm-td-habit">
                            <textarea className="tm-textarea-habit2"></textarea>
                        </td>
                        <td className="tm-td-habit">
                            <textarea className="tm-textarea-habit3" placeholder="Physical fitness time"></textarea>
                            <textarea className="tm-textarea-habit3-margin"
                                      placeholder="Emotional wellbeing"></textarea>
                            <textarea className="tm-textarea-habit3-margin"
                                      placeholder="Improving physical and mental fitness"></textarea>
                        </td>
                        <td className="tm-td-habit">
                            <textarea className="tm-textarea-habit4" placeholder="Social time"></textarea>
                            <textarea className="tm-textarea-habit4-margin"
                                      placeholder="Do you make to-do lists? Why/why not?"></textarea>
                        </td>
                    </tr>
                </table>

                <h2 className="tm-map-header">Time Map</h2>

                <div className="tm-map-flex">
                    <div className="tm-more-time">
                        One thing I would like to spend <span className="tm-more-time-span">more time</span> on:<br/>
                        <textarea className="tm-more-time-textarea"></textarea>
                    </div>
                    <div className="tm-less-time">
                        One thing I would like to spend <span className="tm-less-time-span">less time</span> on:<br/>
                        <textarea className="tm-less-time-textarea"></textarea>
                    </div>
                </div>

                <h3 className="tm-plan-header">How do you plan your day?</h3>
                <p className="tm-plan-desc">Use the clocks to map the activities you do in a day. Later, think about the
                    following questions:</p>
                <ul>
                    <li>Is there any activity you should be spending <strong>more</strong> time on?</li>
                    <li>Is there any activity you should be spending <strong>less</strong> time on?</li>
                </ul>
                <p className="tm-followup-desc">As a follow-up, ask any peer to comment on your schedule and answer the
                    questions above.</p>

                <div className="tm-am-row">
                    <div className="tm-am-label">AM</div>
                    <div className="tm-am-svg">
                        <svg viewBox="0 0 100 100" width="250" height="250">
                            <circle cx="50" cy="50" r="45" stroke="#333" stroke-width="2" fill="none"/>
                            <text x="48" y="12" font-size="6" fill="#333">12</text>
                            <text x="85" y="55" font-size="6" fill="#333">3</text>
                            <text x="48" y="95" font-size="6" fill="#333">6</text>
                            <text x="10" y="55" font-size="6" fill="#333">9</text>
                            <line x1="50" y1="50" x2="50" y2="20" stroke="#333" stroke-width="2"/>
                            <line x1="50" y1="50" x2="80" y2="50" stroke="#333" stroke-width="2"/>
                        </svg>
                    </div>
                </div>

                <div className="tm-pm-row">
                    <div className="tm-pm-label">PM</div>
                    <div className="tm-pm-svg">
                        <svg viewBox="0 0 100 100" width="250" height="250">
                            <circle cx="50" cy="50" r="45" stroke="#333" stroke-width="2" fill="none"/>
                            <text x="48" y="12" font-size="6" fill="#333">12</text>
                            <text x="85" y="55" font-size="6" fill="#333">3</text>
                            <text x="48" y="95" font-size="6" fill="#333">6</text>
                            <text x="10" y="55" font-size="6" fill="#333">9</text>
                            <line x1="50" y1="50" x2="50" y2="30" stroke="#333" stroke-width="2"/>
                            <line x1="50" y1="50" x2="70" y2="70" stroke="#333" stroke-width="2"/>
                        </svg>
                    </div>
                </div>
            </div>


            {/* Eligibility Message */}
            {/*<Alert*/}
            {/*    message={eligibilityMessage}*/}
            {/*    type={isEligible ? "success" : "warning"}*/}
            {/*    showIcon*/}
            {/*    style={{marginBottom: 16}}*/}
            {/*/>*/}

            {/* If Eligible, Show the Form */}
            {/*{isEligible ? (*/}
            {/*    <Form*/}
            {/*        layout="vertical"*/}
            {/*        form={form}*/}
            {/*        onFinish={handleSubmit}*/}
            {/*        initialValues={latestSubmission ? latestSubmission.qna : {}}*/}
            {/*    >*/}
            {/*        /!* 1. Assessing Current Time Management Habits *!/*/}
            {/*        <Title level={4}>1. Assessing Current Time Management Habits</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="How often do you follow a daily schedule?"*/}
            {/*            name="Assessing Current Time Management Habits - How often do you follow a daily schedule?"*/}
            {/*            rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*        >*/}
            {/*            <Input placeholder="e.g., Rarely, Sometimes, Usually, Almost Always"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How do you prioritize tasks or activities for the day?"*/}
            {/*            name="Assessing Current Time Management Habits - How do you prioritize tasks or activities for the day?"*/}
            {/*            rules={[{required: true, message: 'Please describe your prioritization method.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="e.g., Use a to-do list, a planner, or decide as you go"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How often do you get distracted while working on tasks?"*/}
            {/*            name="Assessing Current Time Management Habits - How often do you get distracted while working on tasks?"*/}
            {/*            rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*        >*/}
            {/*            <Input placeholder="e.g., Rarely, Sometimes, Usually, Almost Always"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How well do you balance your schoolwork with other activities like sports, hobbies, or socializing?"*/}
            {/*            name="Assessing Current Time Management Habits - How well do you balance your schoolwork with other activities like sports, hobbies, or socializing?"*/}
            {/*            rules={[{required: true, message: 'Please describe your balance.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3}*/}
            {/*                      placeholder="e.g., I spend too much time on one area and neglect others or I balance them fairly well"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Are you comfortable dealing with multiple tasks or deadlines at the same time?"*/}
            {/*            name="Assessing Current Time Management Habits - Are you comfortable dealing with multiple tasks or deadlines at the same time?"*/}
            {/*            rules={[{required: true, message: 'Please describe your comfort level.'}]}*/}
            {/*        >*/}
            {/*            <Input placeholder="e.g., Yes, I manage well or No, it becomes overwhelming"/>*/}
            {/*        </Form.Item>*/}

            {/*        /!* 2. Analyzing Current Time Usage *!/*/}
            {/*        <Title level={4}>2. Analyzing Current Time Usage</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="What time of the day do you feel most productive?"*/}
            {/*            name="Analyzing Current Time Usage - What time of the day do you feel most productive?"*/}
            {/*            rules={[{required: true, message: 'Please specify your most productive time.'}]}*/}
            {/*        >*/}
            {/*            <Input placeholder="e.g., Morning, Afternoon, Evening, or Night"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How much time do you spend on school-related tasks daily?"*/}
            {/*            name="Analyzing Current Time Usage - How much time do you spend on school-related tasks daily?"*/}
            {/*            rules={[{required: true, message: 'Please specify time spent on school-related tasks.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="e.g., 2 hours for homework, 1 hour for group projects"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How much time do you dedicate to Studying?"*/}
            {/*            name="Analyzing Current Time Usage - How much time do you dedicate to Studying?"*/}
            {/*            rules={[{required: true, message: 'Please specify time dedicated to studying.'}]}*/}
            {/*        >*/}
            {/*            <Input placeholder="e.g., 3 hours"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How much time do you dedicate to Playing/sports?"*/}
            {/*            name="Analyzing Current Time Usage - How much time do you dedicate to Playing/sports?"*/}
            {/*            rules={[{required: true, message: 'Please specify time dedicated to playing/sports.'}]}*/}
            {/*        >*/}
            {/*            <Input placeholder="e.g., 1 hour"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How much time do you dedicate to Socializing with friends or family?"*/}
            {/*            name="Analyzing Current Time Usage - How much time do you dedicate to Socializing with friends or family?"*/}
            {/*            rules={[{required: true, message: 'Please specify time dedicated to socializing.'}]}*/}
            {/*        >*/}
            {/*            <Input placeholder="e.g., 1.5 hours"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How much time do you spend on physical health and fitness each day?"*/}
            {/*            name="Analyzing Current Time Usage - How much time do you spend on physical health and fitness each day?"*/}
            {/*            rules={[{required: true, message: 'Please specify time spent on physical health and fitness.'}]}*/}
            {/*        >*/}
            {/*            <Input placeholder="e.g., 30 minutes for a workout, 15 minutes for a walk"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Do you take regular breaks while studying? If yes, how long and how often?"*/}
            {/*            name="Analyzing Current Time Usage - Do you take regular breaks while studying? If yes, how long and how often?"*/}
            {/*            rules={[{required: true, message: 'Please describe your break schedule.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2}*/}
            {/*                      placeholder="e.g., I take a 10-minute break after every 1-hour study session"/>*/}
            {/*        </Form.Item>*/}

            {/*        /!* 3. Identifying Time Management Challenges *!/*/}
            {/*        <Title level={4}>3. Identifying Time Management Challenges</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="What activities take up most of your time during the day?"*/}
            {/*            name="Identifying Time Management Challenges - What activities take up most of your time during the day?"*/}
            {/*            rules={[{required: true, message: 'Please describe time-consuming activities.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="e.g., Scrolling through social media, playing video games"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Do you often feel rushed or stressed because of poor time management?"*/}
            {/*            name="Identifying Time Management Challenges - Do you often feel rushed or stressed because of poor time management?"*/}
            {/*            rules={[{required: true, message: 'Please describe your feelings related to time management.'}]}*/}
            {/*        >*/}
            {/*            <Input placeholder="e.g., Yes, especially when I leave assignments until the last minute"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Are there specific tasks that you procrastinate on? If so, why?"*/}
            {/*            name="Identifying Time Management Challenges - Are there specific tasks that you procrastinate on? If so, why?"*/}
            {/*            rules={[{required: true, message: 'Please describe tasks you procrastinate on and reasons.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2}*/}
            {/*                      placeholder="e.g., I procrastinate on writing essays because they feel overwhelming"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What distractions make it difficult for you to stay focused?"*/}
            {/*            name="Identifying Time Management Challenges - What distractions make it difficult for you to stay focused?"*/}
            {/*            rules={[{required: true, message: 'Please describe your distractions.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="e.g., Notifications on my phone, noise in my environment"/>*/}
            {/*        </Form.Item>*/}

            {/*        /!* 4. Setting Time Management Goals *!/*/}
            {/*        <Title level={4}>4. Setting Time Management Goals</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="What is one thing you would like to spend more time on?"*/}
            {/*            name="Setting Time Management Goals - What is one thing you would like to spend more time on?"*/}
            {/*            rules={[{required: true, message: 'Please specify what you want to spend more time on.'}]}*/}
            {/*        >*/}
            {/*            <Input placeholder="e.g., Studying math, practicing guitar, or exercising regularly"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What is one thing you would like to spend less time on?"*/}
            {/*            name="Setting Time Management Goals - What is one thing you would like to spend less time on?"*/}
            {/*            rules={[{required: true, message: 'Please specify what you want to spend less time on.'}]}*/}
            {/*        >*/}
            {/*            <Input placeholder="e.g., Watching TV, scrolling social media"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How can you ensure that you dedicate time to activities that help you grow?"*/}
            {/*            name="Setting Time Management Goals - How can you ensure that you dedicate time to activities that help you grow?"*/}
            {/*            rules={[{*/}
            {/*                required: true,*/}
            {/*                message: 'Please describe how you will dedicate time to growth activities.'*/}
            {/*            }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2}*/}
            {/*                      placeholder="e.g., By setting specific times for studying or practicing my hobbies"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Are there any activities you currently spend time on that are not productive or beneficial?"*/}
            {/*            name="Setting Time Management Goals - Are there any activities you currently spend time on that are not productive or beneficial?"*/}
            {/*            rules={[{required: true, message: 'Please describe unproductive activities.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2}*/}
            {/*                      placeholder="e.g., I spend too much time gaming and not enough time studying"/>*/}
            {/*        </Form.Item>*/}

            {/*        /!* 5. Creating a Time Map *!/*/}
            {/*        <Title level={4}>5. Creating a Time Map</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="How do you currently plan your day?"*/}
            {/*            name="Creating a Time Map - How do you currently plan your day?"*/}
            {/*            rules={[{required: true, message: 'Please describe your daily planning method.'}]}*/}
            {/*        >*/}
            {/*            <Input placeholder="e.g., I plan in the morning, night before, or not at all"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="If you divided your day into hours, how much time would you ideally spend on Schoolwork?"*/}
            {/*            name="Creating a Time Map - If you divided your day into hours, how much time would you ideally spend on Schoolwork?"*/}
            {/*            rules={[{required: true, message: 'Please specify ideal time for schoolwork.'}]}*/}
            {/*        >*/}
            {/*            <Input placeholder="e.g., 4 hours"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="If you divided your day into hours, how much time would you ideally spend on Rest or relaxation?"*/}
            {/*            name="Creating a Time Map - If you divided your day into hours, how much time would you ideally spend on Rest or relaxation?"*/}
            {/*            rules={[{required: true, message: 'Please specify ideal time for rest or relaxation.'}]}*/}
            {/*        >*/}
            {/*            <Input placeholder="e.g., 2 hours"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="If you divided your day into hours, how much time would you ideally spend on Physical activity?"*/}
            {/*            name="Creating a Time Map - If you divided your day into hours, how much time would you ideally spend on Physical activity?"*/}
            {/*            rules={[{required: true, message: 'Please specify ideal time for physical activity.'}]}*/}
            {/*        >*/}
            {/*            <Input placeholder="e.g., 1 hour"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="If you divided your day into hours, how much time would you ideally spend on Social activities?"*/}
            {/*            name="Creating a Time Map - If you divided your day into hours, how much time would you ideally spend on Social activities?"*/}
            {/*            rules={[{required: true, message: 'Please specify ideal time for social activities.'}]}*/}
            {/*        >*/}
            {/*            <Input placeholder="e.g., 1 hour"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Are there any time gaps or unproductive periods in your current schedule?"*/}
            {/*            name="Creating a Time Map - Are there any time gaps or unproductive periods in your current schedule?"*/}
            {/*            rules={[{required: true, message: 'Please describe any unproductive periods.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="e.g., I often waste time between classes or before dinner"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How can you rearrange your daily schedule to make it more productive?"*/}
            {/*            name="Creating a Time Map - How can you rearrange your daily schedule to make it more productive?"*/}
            {/*            rules={[{required: true, message: 'Please describe how you will rearrange your schedule.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2}*/}
            {/*                      placeholder="e.g., Spend the first hour after school doing homework, and relax later"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How will you use a time map to stay consistent with your schedule?"*/}
            {/*            name="Creating a Time Map - How will you use a time map to stay consistent with your schedule?"*/}
            {/*            rules={[{required: true, message: 'Please describe how you will use a time map.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2}*/}
            {/*                      placeholder="e.g., I’ll review my time map daily to ensure I’m staying on track"/>*/}
            {/*        </Form.Item>*/}

            {/*        /!* 6. Building Better Time Management Habits *!/*/}
            {/*        <Title level={4}>6. Building Better Time Management Habits</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="What tools or methods can you use to manage your time better?"*/}
            {/*            name="Building Better Time Management Habits - What tools or methods can you use to manage your time better?"*/}
            {/*            rules={[{required: true, message: 'Please describe tools or methods to manage time.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2}*/}
            {/*                      placeholder="e.g., Using a planner, setting reminders on my phone, or making a daily to-do list"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How will you ensure you stick to your schedule when unexpected events occur?"*/}
            {/*            name="Building Better Time Management Habits - How will you ensure you stick to your schedule when unexpected events occur?"*/}
            {/*            rules={[{*/}
            {/*                required: true,*/}
            {/*                message: 'Please describe how you will stick to your schedule during unexpected events.'*/}
            {/*            }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2}*/}
            {/*                      placeholder="e.g., Leave some flexible time in my schedule to handle emergencies"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How can you make sure you take regular breaks without losing focus?"*/}
            {/*            name="Building Better Time Management Habits - How can you make sure you take regular breaks without losing focus?"*/}
            {/*            rules={[{required: true, message: 'Please describe how you will take regular breaks.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2}*/}
            {/*                      placeholder="e.g., Set timers for study and break intervals, like 25 minutes studying and 5 minutes break"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How can you reward yourself for following your schedule?"*/}
            {/*            name="Building Better Time Management Habits - How can you reward yourself for following your schedule?"*/}
            {/*            rules={[{required: true, message: 'Please describe your rewards for following the schedule.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2}*/}
            {/*                      placeholder="e.g., Watch an episode of my favorite show or spend time with friends after completing all tasks"/>*/}
            {/*        </Form.Item>*/}

            {/*        /!* 7. Reflection on Time Management *!/*/}
            {/*        <Title level={4}>7. Reflection on Time Management</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="When you followed a planned schedule, how did it improve your day?"*/}
            {/*            name="Reflection on Time Management - When you followed a planned schedule, how did it improve your day?"*/}
            {/*            rules={[{required: true, message: 'Please describe improvements from following a schedule.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="e.g., I finished my homework early and had more time to relax"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What was one day where your time management didn’t work? What could you have done differently?"*/}
            {/*            name="Reflection on Time Management - What was one day where your time management didn’t work? What could you have done differently?"*/}
            {/*            rules={[{required: true, message: 'Please describe a day where time management failed.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2}*/}
            {/*                      placeholder="e.g., I wasted a lot of time on social media; I could have set limits for phone usage"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What is one small change you can make to improve your time management starting today?"*/}
            {/*            name="Reflection on Time Management - What is one small change you can make to improve your time management starting today?"*/}
            {/*            rules={[{*/}
            {/*                required: true,*/}
            {/*                message: 'Please describe a small change to improve time management.'*/}
            {/*            }]}*/}
            {/*        >*/}
            {/*            <Input placeholder="e.g., I’ll prepare a to-do list every morning"/>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Submit Button *!/*/}
            {/*        <Form.Item>*/}
            {/*            <Button type="primary" className="bg-yellow-500 hover:bg-yellow-600 text-black"*/}
            {/*                    htmlType="submit" loading={isSubmitting}>*/}
            {/*                Submit*/}
            {/*            </Button>*/}
            {/*        </Form.Item>*/}
            {/*    </Form>*/}
            {/*) : (*/}
            {/*    <Text type="warning">*/}
            {/*        You can only submit this form once every 15 days. Please come back later.*/}
            {/*    </Text>*/}
            {/*)}*/}

            {/* Display Latest Submission */}
            {latestSubmission && isEligible && (
                <Card title="Your Latest Submission" style={{marginTop: 24}}>
                    {Object.entries(latestSubmission.qna).map(([question, answer], index) => (
                        <div key={index} style={{marginBottom: 16}}>
                            <Text strong>{question}</Text>
                            <br/>
                            <Text>{answer}</Text>
                        </div>
                    ))}
                </Card>
            )}
        </Card>
    );

};

export default TimeManagementPartA;
